import { Box, Typography } from '@mui/material';
import React from 'react';

type SprintHeatTableProps = {
    riders: string[][];
    heat1TimeIndex: number;
    heat2TimeIndex: number;
    deciderTimeIndex: number;
    countryColIndex: number;
    getFlagUrl: (country: string) => string;
};

export const SprintMobile = ({
    riders,
    heat1TimeIndex,
    heat2TimeIndex,
    deciderTimeIndex,
    countryColIndex,
    getFlagUrl,
}: SprintHeatTableProps) => {
    
    // ✅ Bezpečně detekujeme existenci sloupců s časy. Kontroluje, jestli sloupec fyzicky existuje a má nějaký text.
    const visibleColumns = {
        heat1: riders.some((r) => r?.[heat1TimeIndex] && String(r[heat1TimeIndex]).trim() !== ''),
        heat2: riders.some((r) => r?.[heat2TimeIndex] && String(r[heat2TimeIndex]).trim() !== ''),
        decider: riders.some((r) => r?.[deciderTimeIndex] && String(r[deciderTimeIndex]).trim() !== ''),
    };

    // ✅ Vytvoříme CSS grid dynamicky jen z těch sloupců, které opravdu mají časy
    const columns = [visibleColumns.heat1 && '1fr', visibleColumns.heat2 && '1fr', visibleColumns.decider && '1fr']
        .filter(Boolean)
        .join(' ');

    const gridMinWidth =
        (visibleColumns.heat1 ? 63 : 0) + (visibleColumns.heat2 ? 63 : 0) + (visibleColumns.decider ? 63 : 0);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {riders.map((rider, i) => {
                if (!rider) return null;

                const rank = rider[0];
                const bib = rider[1];
                // Bezpečný fallback, kdyby country v tabulce chybělo
                const country = rider[countryColIndex] || '';

                const heat1 = rider[heat1TimeIndex];
                const heat2 = rider[heat2TimeIndex];
                const decider = rider[deciderTimeIndex];

                // Dynamická kontrola pro texty "WINNER"
                const isH1Winner = (heat1TimeIndex > 0 && rider[heat1TimeIndex - 1]?.toString().toUpperCase() === 'WINNER') || rider[5] === 'WINNER';
                const isH2Winner = (heat2TimeIndex > 0 && rider[heat2TimeIndex - 1]?.toString().toUpperCase() === 'WINNER') || rider[7] === 'WINNER';
                const isDWinner = (deciderTimeIndex > 0 && rider[deciderTimeIndex - 1]?.toString().toUpperCase() === 'WINNER') || rider[9] === 'WINNER';

                return (
                    <Box
                        key={i}
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 0.75,
                            borderRadius: 2,
                        }}
                    >
                        {/* LEFT: rank + name + flag */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.5,
                                flex: 1,
                                minWidth: 0,
                            }}
                        >
                            {/* Rank */}
                            <Typography
                                fontWeight={700}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    textAlign: 'center',
                                }}
                            >
                                {rank === 'DNF' || rank === 'DNS' ? '' : (rank ? '# ' : '')}
                                {rank}
                                <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.1 }}>
                                    {bib}
                                </Typography>
                            </Typography>

                            {/* Name */}
                            <Box sx={{ flex: 1 }}>
                                <Typography fontWeight={600} sx={{ lineHeight: 1.1 }}>
                                    {rider[2]}
                                </Typography>
                                <Typography sx={{ fontSize: '0.75rem', pl: 1, color: 'text.secondary', lineHeight: 1.1 }}>
                                    {rider[3]}
                                </Typography>
                            </Box>

                            {/* Flag */}
                            <Box sx={{ width: 28, display: 'flex', justifyContent: 'center', marginRight: '1.5rem' }}>
                                {country && (
                                    <img
                                        src={getFlagUrl(country)}
                                        alt={country}
                                        style={{ width: '24px', height: '17px', objectFit: 'cover' }}
                                    />
                                )}
                            </Box>
                        </Box>

                        {/* RIGHT: Heat grid */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: columns,
                                gap: 1,
                                minWidth: gridMinWidth || 63,
                                justifyContent: 'end',
                                textAlign: 'right',
                            }}
                        >
                            {visibleColumns.heat1 && (
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: isH1Winner ? 'success.main' : 'text.primary',
                                        paddingRight: '0.5rem',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isH1Winner && <div>WINNER</div>}
                                    {heat1 || ''}
                                </Typography>
                            )}

                            {visibleColumns.heat2 && (
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: isH2Winner ? 'success.main' : 'text.primary',
                                        paddingRight: visibleColumns.decider ? '0.5rem' : '',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isH2Winner && <div>WINNER</div>}
                                    {heat2 || ''}
                                </Typography>
                            )}

                            {visibleColumns.decider && (
                                <Typography
                                    sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 600,
                                        color: isDWinner ? 'success.main' : 'text.primary',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        textAlign: 'center',
                                    }}
                                >
                                    {isDWinner && <div>WINNER</div>}
                                    {decider || ''}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
};